import { searchShowOnMapClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import TextLink from 'LuxKit/TextLink'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import Tooltip from 'LuxKit/Tooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatInlineList from 'components/utils/Formatters/FormatInlineList'
import GeoContext from 'contexts/geoContext'
import React, { MouseEventHandler, useCallback, useContext } from 'react'
import * as Analytics from 'analytics/analytics'
import { getSearchDistanceLabel } from 'components/OfferList/OfferListTiles/Support/searchDistanceLabel'

interface Props {
  locations: Array<string>
  mapUrl?: string
  /** Straight-line distance (in metres) from the search target to this offer; only present for point-based searches. */
  distance?: number
  /** Display name of the point-based search target (e.g. landmark, property, address). */
  searchTargetLabel?: string
}

function SearchTileRowOverlineLocation({ locations, mapUrl, distance, searchTargetLabel }: Props) {
  const { currentRegionCode } = useContext(GeoContext)

  const mapViewClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.stopPropagation()
    Analytics.trackClientEvent({
      subject: 'show_on_map',
      action: 'clicked',
      type: 'interaction',
      category: 'vertical_page',
    })

    addGTMEvent(searchShowOnMapClick())
  }, [])

  return <>
    <BodyText variant="small" colour="neutral-two">
      <FormatInlineList list={locations} type="unit" />
      {!!mapUrl && <>{' '}&middot; <TextLink weight="regular" to={mapUrl} onClick={mapViewClick}>
        Show on map
      </TextLink>
      </>}
    </BodyText>
    {!!distance && !!searchTargetLabel && <Tooltip
      description="This distance is calculated as a straight line on a map. Actual travel distance may vary."
    >
      <CaptionTextBlock
        variant="medium"
        colour="neutral-three"
        startIcon={<LineMapMarkerIcon />}
        data-testid="search-tile-distances"
      >
        {getSearchDistanceLabel(searchTargetLabel, distance, currentRegionCode)}
      </CaptionTextBlock>
    </Tooltip>}
  </>
}

export default SearchTileRowOverlineLocation
